import { goAnchor } from "./app-nav";
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, MoreHorizontal, Search, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { t, tn } from "@/lib/i18n";
import WorkflowPanel from "./workflows-panel";
import { TemplateDetail, TemplatesView } from "./workflows-templates";
import WorkflowEditor from "./workflows-editor";
import {
  demoWorkflows,
  workflowEmpty,
  workflowHints,
  workflowStatuses,
  workflowTemplates,
  workflowTriggers,
  type WorkflowRecord,
  type WorkflowStatus,
  type WorkflowTemplate,
} from "@/mocks/workflows";

/* Живой макет окна «Рабочие процессы»: пустое состояние, галерея шаблонов с категориями,
   карточка шаблона со схемой шагов и редактор созданного процесса. Демо-данные включаются в шапке окна. */

const RAIL = [
  { id: "workflows", icon: "ri-flow-chart", label: t("Рабочие процессы"), anchor: "#workflows", active: true },
  { id: "reports", icon: "ri-bar-chart-2-line", label: t("Отчеты"), anchor: "#reports", active: false },
  { id: "settings", icon: "ri-settings-3-line", label: t("Настройки"), anchor: "#settings", active: false },
  { id: "broadcasts", icon: "ri-mail-send-line", label: t("Рассылки"), anchor: "#broadcasts", active: false },
  { id: "inbox", icon: "ri-inbox-line", label: t("Входящие"), anchor: "#inbox-app", active: false },
  { id: "contacts", icon: "ri-contacts-book-2-line", label: t("Контакты"), anchor: "#contacts-app", active: false },
  { id: "dashboard", icon: "ri-dashboard-line", label: t("Дашборд"), anchor: "#dashboard", active: false },
  { id: "channels", icon: "ri-share-forward-line", label: t("Каналы"), anchor: "#channels", active: false },
  { id: "ai-agents", icon: "ri-robot-2-line", label: t("AI-агенты"), anchor: "#ai-agents", active: false },
  { id: "ai-assist", icon: "ri-sparkling-line", label: "AI Assist", anchor: "#ai-assist", active: false },
];

const statusMeta = (id: WorkflowStatus) => workflowStatuses.find((s) => s.id === id);
const templateName = (id: string | null) => (id ? workflowTemplates.find((t) => t.id === id)?.name ?? t("шаблон") : null);

const fmt = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
};

function EmptyArt() {
  return (
    <div className="relative mx-auto w-full max-w-[20rem]" aria-hidden>
      <div className="mx-auto w-[9.5rem] rounded-btn border border-primary/25 bg-primary/10 p-2">
        <span className="flex items-center gap-1">
          <span className="size-1.5 rounded-full bg-primary/60" />
          <span className="h-1 w-8 rounded-full bg-primary/35" />
        </span>
        <span className="mt-1.5 block h-1 w-full rounded-full bg-primary/30" />
        <span className="mt-1 block h-1 w-3/4 rounded-full bg-primary/25" />
      </div>

      <div className="mx-auto h-4 w-px bg-border" />
      <div className="relative mx-auto h-px w-[12.5rem] bg-border">
        <span className="absolute start-0 top-0 h-3 w-px bg-border" />
        <span className="absolute end-0 top-0 h-3 w-px bg-border" />
      </div>
      <div className="mx-auto mt-3 flex w-full items-start justify-center gap-6">
        <div className="w-[6.5rem] rounded-btn border border-primary/25 bg-primary/10 p-2">
          <span className="flex items-center gap-1">
            <i className="ri-star-line text-[10px] leading-none text-primary" />
            <span className="h-1 w-8 rounded-full bg-primary/35" />
          </span>
          <span className="mt-1.5 block h-1 w-full rounded-full bg-primary/30" />
          <span className="mt-1 block h-1 w-2/3 rounded-full bg-primary/25" />
        </div>
        <div className="w-[6.5rem] rounded-btn border border-red-500/30 bg-red-500/10 p-2">
          <span className="flex items-center gap-1">
            <i className="ri-alert-line text-[10px] leading-none text-red-500" />
            <span className="h-1 w-8 rounded-full bg-red-500/40" />
          </span>
          <span className="mt-1.5 block h-1 w-full rounded-full bg-red-500/30" />
          <span className="mt-1 block h-1 w-3/4 rounded-full bg-red-500/25" />
        </div>
      </div>
    </div>
  );
}

function WorkflowsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [view, setView] = useState<"list" | "templates" | "template" | "workflow">("list");
  const [template, setTemplate] = useState<WorkflowTemplate | null>(null);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [category, setCategory] = useState("all");
  const [tQuery, setTQuery] = useState("");
  const [query, setQuery] = useState("");
  const [demo, setDemo] = useState(false);
  const [created, setCreated] = useState<WorkflowRecord[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);
  const [panel, setPanel] = useState<null | { template?: WorkflowTemplate | null }>(null);
  const [menu, setMenu] = useState(false);
  const [addMenu, setAddMenu] = useState(false);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const menuWrap = useRef<HTMLDivElement | null>(null);
  const addWrap = useRef<HTMLDivElement | null>(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!menu && !addMenu) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuWrap.current && !menuWrap.current.contains(target)) setMenu(false);
      if (addWrap.current && !addWrap.current.contains(target)) setAddMenu(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menu, addMenu]);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  };

  const all = useMemo(() => {
    const base = demo ? demoWorkflows.filter((w) => !removed.includes(w.id)) : [];
    const overrides = new Map(created.map((w) => [w.id, w]));
    const merged = base.map((w) => overrides.get(w.id) ?? w);
    const extra = created.filter((w) => !removed.includes(w.id) && !base.some((d) => d.id === w.id));
    return [...merged, ...extra];
  }, [demo, created, removed]);

  const current = all.find((w) => w.id === currentId) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? all.filter((w) => w.name.toLowerCase().includes(q) || (templateName(w.template) ?? "").toLowerCase().includes(q)) : all;
  }, [all, query]);

  const jump = (anchor: string, label: string) => {
    if (goAnchor(anchor)) return;
    flash(t("Раздел «{label}» в демо не открывается", { label }));
  };

  const openTemplates = () => {
    setView("templates");
    setTemplate(null);
    setCurrentId(null);
  };

  const openTemplate = (t: WorkflowTemplate) => {
    setTemplate(t);
    setView("template");
  };

  const create = (name: string, triggerId: string, from: WorkflowTemplate | null) => {
    const triggerLabel = workflowTriggers.find((t) => t.id === triggerId)?.label ?? t("Разговор начался");
    const stamp = Date.now();
    const steps = from
      ? from.steps.map((s, i) => ({ ...s, id: `nwf-${stamp}-${i}` }))
      : [{ id: `nwf-${stamp}-0`, kind: "trigger" as const, title: t("Курок"), text: triggerLabel }];
    const record: WorkflowRecord = {
      id: `wf-new-${stamp}`,
      name,
      status: "draft",
      template: from?.id ?? null,
      steps,
      author: t("Вы"),
      updated: "2026-10-29",
    };
    setCreated((prev) => [...prev, record]);
    setPanel(null);
    setCurrentId(record.id);
    setView("workflow");
    setQuery("");
    flash(
      from
        ? t("Процесс «{name}» создан из шаблона «{template}» — проверьте шаги и включите его", { name, template: from.name })
        : t("Процесс «{name}» создан: добавьте шаги к триггеру «{trigger}»", { name, trigger: triggerLabel }),
    );
  };

  const toggleStatus = (id: string) => {
    const record = all.find((w) => w.id === id);
    if (!record) return;
    const next: WorkflowRecord = { ...record, status: record.status === "active" ? "paused" : "active", updated: "2026-10-29" };
    setCreated((prev) => (prev.some((w) => w.id === id) ? prev.map((w) => (w.id === id ? next : w)) : [...prev, next]));
    flash(
      next.status === "active"
        ? t("Процесс «{name}» включён и начал выполняться", { name: record.name })
        : t("Процесс «{name}» приостановлен и больше не запускается", { name: record.name }),
    );
  };

  const removeWorkflow = (id: string) => {
    const record = all.find((w) => w.id === id);
    if (!record) return;
    setRemoved((prev) => [...prev, id]);
    setCreated((prev) => prev.filter((w) => w.id !== id));
    setCurrentId(null);
    setView("list");
    flash(t("Процесс «{name}» удалён вместе со всеми шагами", { name: record.name }));
  };

  const toggleDemo = () => {
    const next = !demo;
    setDemo(next);
    if (next) {
      flash(t("Демо-данные включены: в рабочем пространстве четыре процесса в разных статусах"));
    } else {
      setCreated((prev) => prev.filter((w) => !demoWorkflows.some((d) => d.id === w.id)));
      setRemoved((prev) => prev.filter((id) => !demoWorkflows.some((d) => d.id === id)));
      setCurrentId(null);
      setView("list");
      flash(t("Демо-данные выключены — процессов снова нет"));
    }
  };

  const activeCount = all.filter((w) => w.status === "active").length;

  return (
    <Band id="workflows" tone="tint">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска окна */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">{str(brand) || "Hub"} / {t("рабочее пространство → рабочие процессы")}</span>
          <span className="ms-auto flex items-center gap-2.5">
            <span className="inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
              <span className={cn("size-1.5 rounded-full", activeCount > 0 ? "bg-emerald-500" : "bg-foreground/30")} aria-hidden />
              {t("Активных: {n}", { n: activeCount })}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="hidden text-[10.5px] font-semibold text-muted-foreground sm:inline">{t("Демо-данные")}</span>
              <button
                type="button"
                role="switch"
                aria-checked={demo}
                aria-label={t("Демо-данные")}
                onClick={toggleDemo}
                className={cn("relative h-5 w-9 flex-none cursor-pointer rounded-full border transition", demo ? "border-primary bg-primary" : "border-border bg-foreground/10")}
              >
                <span className={cn("absolute top-1/2 size-3.5 -translate-y-1/2 rounded-full bg-card transition-all", demo ? "start-[1.15rem]" : "start-[0.15rem]")} aria-hidden />
              </button>
            </span>
          </span>
        </div>

        {/* пробный период */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-primary/[.08] px-3.5 py-2 text-[11.5px]">
          <i className="ri-information-line text-[13px] leading-none text-primary" aria-hidden />
          <span className="min-w-0 flex-1 leading-relaxed text-foreground/80">
            {t("Пробный период Growth Plan закончится 3 ноября 2026 — через 6 дней. Обновите тариф, чтобы не потерять доступ.")}
          </span>
          <button
            type="button"
            onClick={() => jump("#onboarding", t("Начало работы"))}
            className="inline-flex h-6 flex-none cursor-pointer items-center whitespace-nowrap rounded-full border border-primary/30 px-2.5 text-[10.5px] font-semibold text-primary transition hover:bg-primary/10"
          >
            {t("Чеклист онбординга")}
          </button>
          <button
            type="button"
            onClick={() => flash(t("Демо-режим: смена тарифа здесь недоступна"))}
            className="inline-flex h-6 flex-none cursor-pointer items-center whitespace-nowrap rounded-full bg-primary px-2.5 text-[10.5px] font-semibold text-primary-foreground transition hover:brightness-110"
          >
            {t("Обновить тариф")}
          </button>
        </div>

        <div className="flex">
          {/* иконная панель */}
          <div className="hidden w-12 flex-none flex-col items-center gap-1 border-e border-border py-3 lg:flex">
            {RAIL.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => jump(r.anchor, r.label)}
                title={r.label}
                aria-label={r.label}
                className={cn(
                  "grid size-8 cursor-pointer place-items-center rounded-btn transition",
                  r.active ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <i className={cn(r.icon, "text-[15px] leading-none")} aria-hidden />
              </button>
            ))}
            <span className="mt-auto grid size-8 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground" aria-hidden>
              {t("В")}
            </span>
          </div>

          {view === "list" ? (
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
                <div className="flex min-w-0 items-center gap-1.5">
                  <h4 className="truncate text-[13px] font-semibold">{t("Рабочие процессы")}</h4>
                  <span className="text-[10.5px] tabular-nums text-muted-foreground">{all.length}</span>
                </div>

                <div className="relative hidden w-52 items-center sm:flex">
                  <Search className="pointer-events-none absolute start-2.5 size-3.5 text-muted-foreground" aria-hidden />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("Поиск")}
                    aria-label={t("Поиск рабочих процессов")}
                    className="h-7 w-full box-border rounded-btn border border-border bg-background ps-8 pe-7 text-[11.5px] text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                  />
                  {query ? (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      aria-label={t("Очистить поиск")}
                      className="absolute end-1.5 grid size-4 cursor-pointer place-items-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground"
                    >
                      <X className="size-3" aria-hidden />
                    </button>
                  ) : null}
                </div>

                <div className="relative ms-auto flex items-center" ref={addWrap}>
                  <button
                    type="button"
                    onClick={() => setPanel({ template: null })}
                    className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-s-btn bg-primary px-2.5 text-[11px] font-semibold text-primary-foreground transition hover:brightness-110"
                  >
                    <i className="ri-add-line text-[13px] leading-none" aria-hidden />
                    <span className="hidden sm:inline">{t("Добавить рабочий процесс")}</span>
                    <span className="sm:hidden">{t("Добавить")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddMenu((v) => !v)}
                    aria-label={t("Варианты создания")}
                    aria-expanded={addMenu}
                    aria-haspopup="menu"
                    className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-e-btn border-s border-primary-foreground/25 bg-primary text-primary-foreground transition hover:brightness-110"
                  >
                    <i className="ri-arrow-down-s-line text-[14px] leading-none" aria-hidden />
                  </button>
                  {addMenu ? (
                    <div role="menu" className="absolute end-0 top-full z-30 mt-1 w-52 rounded-btn border border-border bg-card p-1 shadow-lift">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setAddMenu(false);
                          setPanel({ template: null });
                        }}
                        className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-1.5 text-start text-[11.5px] text-foreground/80 transition hover:bg-muted hover:text-foreground"
                      >
                        <i className="ri-magic-line text-[12px] leading-none text-muted-foreground" aria-hidden />
                        {t("Начать с нуля")}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setAddMenu(false);
                          openTemplates();
                        }}
                        className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-1.5 text-start text-[11.5px] text-foreground/80 transition hover:bg-muted hover:text-foreground"
                      >
                        <i className="ri-layout-grid-line text-[12px] leading-none text-muted-foreground" aria-hidden />
                        {t("Из шаблона")}
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="relative" ref={menuWrap}>
                  <button
                    type="button"
                    onClick={() => setMenu((v) => !v)}
                    aria-label={t("Ещё действия")}
                    aria-expanded={menu}
                    aria-haspopup="menu"
                    className="grid size-7 cursor-pointer place-items-center rounded-btn border border-border text-muted-foreground transition hover:border-primary/50 hover:text-primary"
                  >
                    <MoreHorizontal className="size-3.5" aria-hidden />
                  </button>
                  {menu ? (
                    <div role="menu" className="absolute end-0 top-full z-30 mt-1 w-56 rounded-btn border border-border bg-card p-1 shadow-lift">
                      {[
                        { label: t("Шаблоны рабочих процессов"), icon: "ri-layout-grid-line" },
                        { label: t("Импорт процессов"), icon: "ri-upload-2-line" },
                        { label: t("Экспорт списка"), icon: "ri-download-2-line" },
                      ].map((m) => (
                        <button
                          key={m.label}
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setMenu(false);
                            if (m.icon === "ri-layout-grid-line") openTemplates();
                            else flash(t("«{label}» в демо-режиме недоступен", { label: m.label }));
                          }}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-1.5 text-start text-[11.5px] text-foreground/80 transition hover:bg-muted hover:text-foreground"
                        >
                          <i className={cn(m.icon, "text-[12px] leading-none text-muted-foreground")} aria-hidden />
                          {m.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="relative flex min-h-[24rem] flex-col">
                {filtered.length > 0 ? (
                  <ul className="divide-y divide-border">
                    {filtered.map((w) => {
                      const meta = statusMeta(w.status);
                      const from = templateName(w.template);
                      return (
                        <li key={w.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setCurrentId(w.id);
                              setView("workflow");
                            }}
                            className="flex w-full cursor-pointer items-center gap-2.5 px-3.5 py-2.5 text-start transition hover:bg-muted/60"
                          >
                            <span className="grid size-7 flex-none place-items-center rounded-btn border border-border bg-background/60 text-primary" aria-hidden>
                              <i className="ri-flow-chart text-[13px] leading-none" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex flex-wrap items-center gap-1.5">
                                <span className="truncate text-[12.5px] font-semibold">{w.name}</span>
                                <span className={cn("inline-flex items-center gap-1 rounded-full px-1.5 py-[1px] text-[9.5px] font-semibold", meta?.pill)}>
                                  <span className={cn("size-1.5 rounded-full", meta?.dot)} aria-hidden />
                                  {meta?.label}
                                </span>
                              </span>
                              <span className="mt-0.5 block truncate text-[10px] text-muted-foreground">
                                {tn(w.steps.filter((s) => s.kind !== "trigger").length, "шаг", "шага", "шагов")} · {from ? t("шаблон «{name}»", { name: from }) : t("с нуля")} ·{" "}
                                {t("обновлён {date}", { date: fmt(w.updated) })}
                              </span>
                            </span>
                            <span className="hidden text-[10.5px] text-muted-foreground sm:block">{w.author}</span>
                            <i className="ri-arrow-right-s-line flex-none text-[15px] leading-none text-muted-foreground" aria-hidden />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="flex flex-1 flex-col items-center justify-center px-5 py-10 text-center">
                    <EmptyArt />
                    <h5 className="mt-6 max-w-md text-[15px] font-semibold leading-snug">
                      {query ? t("По запросу «{query}» ничего не нашлось", { query }) : workflowEmpty.title}
                    </h5>
                    <p className="mt-1.5 max-w-lg text-[11.5px] leading-relaxed text-muted-foreground">
                      {query ? t("Попробуйте другое название или очистите поиск — процессы ищутся и по названию шаблона.") : workflowEmpty.text}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                      {query ? (
                        <button
                          type="button"
                          onClick={() => setQuery("")}
                          className="inline-flex h-8 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border bg-card px-3.5 text-[11.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
                        >
                          {t("Очистить поиск")}
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setPanel({ template: null })}
                            className="inline-flex h-8 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border bg-card px-3.5 text-[11.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
                          >
                            {workflowEmpty.scratch}
                          </button>
                          <button
                            type="button"
                            onClick={openTemplates}
                            className="inline-flex h-8 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn bg-primary px-3.5 text-[11.5px] font-semibold text-primary-foreground transition hover:brightness-110"
                          >
                            {workflowEmpty.templates}
                          </button>
                        </>
                      )}
                    </div>
                    {!demo ? (
                      <button
                        type="button"
                        onClick={toggleDemo}
                        className="mt-4 inline-flex cursor-pointer items-center gap-1 text-[10.5px] font-semibold text-primary underline decoration-dotted underline-offset-2 lg:hidden"
                      >
                        <i className="ri-play-circle-line text-[12px] leading-none" aria-hidden />
                        {t("Включить демо-данные")}
                      </button>
                    ) : null}
                  </div>
                )}

                {toast ? (
                  <div
                    role="status"
                    className="pointer-events-none absolute inset-x-3 bottom-3 z-30 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
                  >
                    <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
                    {toast}
                  </div>
                ) : null}
              </div>

              <div className="grid gap-2 border-t border-border p-3.5 sm:grid-cols-3">
                {workflowHints.map((h) => (
                  <div key={h.title} className="rounded-btn border border-border bg-background/40 p-2.5">
                    <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                      <i className={cn(h.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                      {h.title}
                    </p>
                    <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{h.text}</p>
                  </div>
                ))}
              </div>

              <p className="px-3.5 pb-3.5 text-[11px] text-muted-foreground">
                {all.length > 0
                  ? t("Всего процессов: {n} · шаблонов в галерее: {templates}. Демо-режим: данные живут только на этой странице.", { n: all.length, templates: workflowTemplates.length })
                  : t("Демо-режим: процессы живут только на этой странице и не сохраняются после перезагрузки.")}
              </p>
            </div>
          ) : null}

          {view === "templates" ? (
            <TemplatesView
              category={category}
              query={tQuery}
              onCategory={setCategory}
              onQuery={setTQuery}
              onOpen={openTemplate}
              onScratch={() => setPanel({ template: null })}
              onBack={() => setView("list")}
            />
          ) : null}

          {view === "template" && template ? (
            <TemplateDetail
              template={template}
              onBack={() => setView("templates")}
              onUse={(t) => setPanel({ template: t })}
              onLearnMore={() => flash(t("Демо-режим: справка о шаблоне «{name}» в макете недоступна", { name: template.name }))}
            />
          ) : null}

          {view === "workflow" && current ? (
            <WorkflowEditor
              workflow={current}
              onBack={() => {
                setCurrentId(null);
                setView("list");
              }}
              onToggle={() => toggleStatus(current.id)}
              onRemove={() => removeWorkflow(current.id)}
              onAddStep={() => flash(t("Демо-режим: добавление шагов в редакторе недоступно"))}
            />
          ) : null}
        </div>

        {panel ? <WorkflowPanel template={panel.template ?? null} onClose={() => setPanel(null)} onCreate={create} /> : null}

        {toast && view !== "list" ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default WorkflowsDemo;